import {
  getPersonDescription,
  getPersonDetailLink,
} from '@/contact/person/utils';
import { DuplicatesCell } from '@/contact/person/view/duplicates-cell.component';
import { PersonEmail } from '@/contact/person/view/person-email.component';
import { UserStatusChip } from '@/contact/person/view/user-status-chip.component';
import { Frame } from '@/frame';
import { FilterPersonInput } from '@/graphql/generated/types';
import { Button } from '@/ui/button/button.component';
import { Chip } from '@/ui/chip/chip.component';
import { DebouncedInput } from '@/ui/debounced-input/debounced-input.component';
import { DropdownMenu } from '@/ui/dropdown-menu/dropdown-menu.component';
import { ListView } from '@/ui/list-view/list-view.component';
import { Row } from '@/ui/table/row.component';
import { Link, RouteComponentProps } from '@reach/router';
import React, { ComponentType } from 'react';
import { getPaginationFromQuery } from 'shared/helpers/pagination.helper';
import { useUrlQuery } from 'shared/hooks/url-query';
import { Translator } from 'shared/translator/translator.component';
import { usePersonActions } from '../../actions/actions.hooks';
import { PersonListDataFragment, usePersonsQuery } from '../list.generated';

export const PersonList: ComponentType<RouteComponentProps> = (props) => {
  const [query] = useUrlQuery<{ take: string; skip: string; query: string }>();

  const _ = Translator.useTranslator();

  const { data, loading, refetch } = usePersonsQuery({
    variables: {
      pagination: getPaginationFromQuery(query),
      withDuplicates: true,
      filter: { query: query.query },
    },
    fetchPolicy: 'network-only',
  });

  const { modalAnonymize, modalDelete, askForAnonymize, askForDelete } =
    usePersonActions(() => refetch());

  return (
    <>
      <Frame.SubTitle>Übersicht</Frame.SubTitle>
      <Frame.ActionBar>
        <Button linkTo="/contacts/persons/create" label="Neuer Kontakt" />
      </Frame.ActionBar>
      <Frame.Content>
        {modalAnonymize}
        {modalDelete}
        <ListView<PersonListDataFragment, FilterPersonInput>
          loading={loading}
          data={data?.persons.data || []}
          totalCount={data?.persons?.totalCount}
          columns={[
            {
              name: 'Name',
              sortable: true,
            },
            {
              name: 'E-Mail',
              sortable: true,
            },
            {
              name: 'Unternehmen',
              sortable: true,
            },
            { name: 'User', sortable: true },
            { name: 'Hinweise', sortable: true },
            { name: 'Actions', sortable: true },
          ]}
          renderFilter={({ value, onChange }) => (
            <DebouncedInput
              debounceRate={400}
              value={value?.query || ''}
              onChange={(value) => onChange('query', value)}
              placeholder={'Suche…'}
            />
          )}
          renderRow={(item) => {
            const detailLink = getPersonDetailLink(item);
            return (
              <Row key={item.id}>
                <td>
                  <Link to={detailLink}>{getPersonDescription(item)}</Link>
                </td>
                <td>
                  <Link to={detailLink}>
                    <PersonEmail person={item} />
                  </Link>
                </td>
                <td>
                  {item.enterprises.length > 0 &&
                    item.enterprises.map((i) => i.name).join(', ')}
                </td>
                <td>
                  {item.user && (
                    <Link to={detailLink}>
                      {item.user.username}
                      <br />
                      <Chip>{_(`user-type.${item.user.__typename}`)}</Chip>
                      <UserStatusChip user={item.user} />
                    </Link>
                  )}
                </td>
                <td>
                  {item.__typename === 'PersonWithDuplicates' &&
                    item.duplicates && (
                      <DuplicatesCell
                        duplicates={item.duplicates}
                        linkTo={`../../${item.id}/duplicates`}
                      />
                    )}
                </td>
                <td>
                  <DropdownMenu
                    actions={[
                      {
                        label: 'Löschen (anonym)',
                        meaning: 'error',
                        onClick: () => askForAnonymize(item),
                      },
                      {
                        label: 'Löschen (destruktiv)',
                        meaning: 'error',
                        onClick: () => askForDelete(item),
                      },
                    ]}
                  />
                </td>
              </Row>
            );
          }}
        />
      </Frame.Content>
    </>
  );
};
